import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import CommonDropdown from "../commonDropDown/CommonDropDown";
import { getProfile } from "../../services/mentorProfile";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Sidebar from "../Sidebar";
import { api1 } from "../../utils/axiosInstance";
import { toast } from "react-toastify";
import CustomButton from "../customButtom/CustomButton";
import { getBankDetails } from "../../services/bankDetail";
import { useQuery } from "@tanstack/react-query";

const BankDetail = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const mentorProfile = useSelector((state) => state.mentor.profile);
  const { data: bankDetails } = useQuery({
    queryKey: ["bankDetails", mentorProfile._id],
    queryFn: () => getBankDetails(mentorProfile._id),
    enabled: !!mentorProfile.paymentAccountId, // Only fetch if userId exists
  });

  const countries = [
    { value: "AU", label: "Australia" },
    { value: "AT", label: "Austria" },
    { value: "BE", label: "Belgium" },
    { value: "BR", label: "Brazil" },
    { value: "BG", label: "Bulgaria" },
    { value: "CA", label: "Canada" },
    { value: "CI", label: "Côte d'Ivoire" },
    { value: "HR", label: "Croatia" },
    { value: "CY", label: "Cyprus" },
    { value: "CZ", label: "Czech Republic" },
    { value: "DK", label: "Denmark" },
    { value: "EE", label: "Estonia" },
    { value: "FI", label: "Finland" },
    { value: "FR", label: "France" },
    { value: "DE", label: "Germany" },
    { value: "GH", label: "Ghana" },
    { value: "GI", label: "Gibraltar" },
    { value: "GR", label: "Greece" },
    { value: "HK", label: "Hong Kong" },
    { value: "HU", label: "Hungary" },
    { value: "IN", label: "India" },
    { value: "ID", label: "Indonesia" },
    { value: "IE", label: "Ireland" },
    { value: "IT", label: "Italy" },
    { value: "JP", label: "Japan" },
    { value: "KE", label: "Kenya" },
    { value: "LV", label: "Latvia" },
    { value: "LI", label: "Liechtenstein" },
    { value: "LT", label: "Lithuania" },
    { value: "LU", label: "Luxembourg" },
    { value: "MY", label: "Malaysia" },
    { value: "MT", label: "Malta" },
    { value: "MX", label: "Mexico" },
    { value: "NL", label: "Netherlands" },
    { value: "NZ", label: "New Zealand" },
    { value: "NG", label: "Nigeria" },
    { value: "NO", label: "Norway" },
    { value: "PL", label: "Poland" },
    { value: "PT", label: "Portugal" },
    { value: "RO", label: "Romania" },
    { value: "SG", label: "Singapore" },
    { value: "SK", label: "Slovakia" },
    { value: "SI", label: "Slovenia" },
    { value: "ZA", label: "South Africa" },
    { value: "ES", label: "Spain" },
    { value: "SE", label: "Sweden" },
    { value: "CH", label: "Switzerland" },
    { value: "TH", label: "Thailand" },
    { value: "AE", label: "United Arab Emirates" },
    { value: "GB", label: "United Kingdom" },
    { value: "US", label: "United States" },
  ];

  useEffect(() => {
    dispatch(getProfile({ userId: id }));
  }, [dispatch, id]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();
  useEffect(() => {
    if (mentorProfile) {
      setValue("paymentCountryCode", mentorProfile.paymentCountryCode || "");
    }
  }, [mentorProfile, setValue]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      if (mentorProfile.paymentAccountId) {
        await onboardAccount();
      } else {
        const payload = {
          ...data,
          email: mentorProfile.email,
          mentor: mentorProfile._id,
        };
        const res = await api1.post(
          `/api/stripe/create-connected-account`,
          payload
        );
        window.location.href = res.data.url;
        dispatch(getProfile({ userId: id }));
        toast.success("Bank details updated successfully!");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error(`Country doesn't support all required capabilities`);
    } finally {
      setLoading(false);
    }
  };
  const onboardAccount = async () => {
    try {
      setLoading(true);
      const res = await api1.get(
        `/api/stripe/onboard-account/${mentorProfile.paymentAccountId}`
      );
      window.location.href = res.data.url;
      dispatch(getProfile({ userId: id }));
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Failed to update bank details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sidebar>
      <div className="text-xl text-custom-pink font-bold my-4">
        Mentorship Account Form
      </div>
      <div className="w-full mx-auto bg-white shadow-lg rounded-lg">
        <div className="flex justify-center px-6 pt-6 w-full">
          <h2 className="text-2xl text-[#6B6B6B] underline font-semibold mb-4">
            Mentorship Payout Account Bank Setup
          </h2>
        </div>
        <div className="px-6 pb-6">
          <p className="font-semibold text-custom-pink">
            Mentor Account Setup – Quick Note{" "}
          </p>
          <p className="mb-5 text-gray-500">
            Welcome aboard! To keep payouts simple and secure, we use Stripe.
            When you finish creating your mentor profile, you’ll be guided
            through Stripe’s setup:
          </p>{" "}
          <p className="mb-5 text-gray-500">
            <span className="font-semibold text-custom-pink">
              Bank details:
            </span>{" "}
            Add the account where you’d like your earnings sent.
          </p>
          <p className="mb-5 text-gray-500">
            <span className="font-semibold text-custom-pink">
              Identity check:
            </span>{" "}
            Stripe will securely verify your government‑issued ID once during
            setup (a standard KYC step).
          </p>
          <p className="mb-5 text-gray-500">
            <span className="font-semibold text-custom-pink">
              Monthly payouts:
            </span>{" "}
            All earnings for a calendar month are bundled and deposited to your
            bank account the following month—no action needed on your end.
          </p>
          <p className="text-gray-500">
            That’s it! You can always see payout status in your mentor
            dashboard. If you run into any issues, just let us know at 
            support@ontrackcareers.ai
          </p>
          <p className="text-gray-500">Looking forward to working with you!</p>
        </div>
        <div className="border-b"></div>
        <div className="space-y-4 p-6">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="grid grid-cols-1 sm:grid-cols-2 gap-4 border-b pb-4"
          >
            {/* Country Dropdown */}
            {mentorProfile?.paymentAccountId && (
              <div className="flex flex-col gap-2">
                <label className="font-bold text-base">Account Id</label>
                <input
                  type="text"
                  defaultValue={mentorProfile.paymentAccountId}
                  readOnly={true}
                  className={`border rounded-lg px-[16px] py-[11px] outline-none transition-all focus:border-blue-300 focus:ring-2 focus:ring-blue-200`}
                />
              </div>
            )}
            <CommonDropdown
              label="Country"
              name="paymentCountryCode"
              required
              defaultValue={bankDetails?.paymentCountryCode}
              register={register}
              readOnly={bankDetails?.paymentCountryCode}
              errors={errors}
              options={countries}
              search={true}
            />

            {/* Submit Button */}
            <div className="mt-10">
              {mentorProfile?.paymentAccountId ? (
                bankDetails?.paymentAccountVerified === false ? (
                  <>
                    {bankDetails?.issue === "tos_acceptance.ip" ? (
                      <div className="mb-5">
                        <p className="text-xs font-bold">
                          Bank Account Verification Status:{" "}
                          <span className="text-red-800 ">
                            Account Verification Not Completed
                          </span>
                        </p>
                        <p className="text-xs font-bold">
                          Id Verification Status:{" "}
                          <span className="text-red-800 ">
                            Id Verification Not Completed
                          </span>
                        </p>
                      </div>
                    ) : (
                      <div className="mb-5">
                        <p className="text-xs font-bold">
                          Bank Account Verification Status:{" "}
                          <span className="text-green-800 ">
                            Account Verification Completed
                          </span>
                        </p>
                        <p className="text-xs font-bold">
                          Id Verification Status:{" "}
                          <span className="text-red-800 ">
                            Id Verification Not Completed
                          </span>
                        </p>
                      </div>
                    )}
                    <CustomButton
                      type="submit"
                      label={
                        bankDetails.issue === "tos_acceptance.ip"
                          ? "Verify Account"
                          : "Verify Identity"
                      }
                      buttonClass="bg-custom-gradient px-[20px] py-[9px] rounded-lg w-full sm:w-[138px]"
                    />
                  </>
                ) : (
                  <>
                    {/* {bankDetails?.issue === "tos_acceptance.ip" ? (
                      <p className="text-xs font-bold">
                        Bank Account Verification Status:{" "}
                        <span className="text-red-800 ">
                          Account Verification Not Completed
                        </span>
                      </p>
                    ) : (
                      <p className="text-xs font-bold">
                        Id Verification Status:{" "}
                        <span className="text-green-800 ">
                          Id Verification Not Completed
                        </span>
                      </p>
                    )} */}

                    <p className="text-xs font-bold">
                      Bank Account Verification Status:{" "}
                      <span className="text-green-800 ">
                        Account Verification Completed
                      </span>
                    </p>
                    <p className="text-xs font-bold">
                      Id Verification Status:{" "}
                      <span className="text-green-800 ">
                        Id Verification Completed
                      </span>
                    </p>
                  </>
                )
              ) : (
                <CustomButton
                  type="submit"
                  label={loading ? "Saving..." : "Submit"}
                  buttonClass="bg-custom-gradient px-[20px] py-[9px] rounded-lg w-full sm:w-[138px]"
                  disabled={loading}
                />
              )}
            </div>
          </form>
        </div>
      </div>
    </Sidebar>
  );
};

export default BankDetail;
